import { Dialog, Grid } from '@mui/material';
import { CancelButtonBlack } from '@tassat/button';
import { ClientMessagesStatusEnum, doesWorkflowRejectCodeExist } from '@tassat/common-definitions';
import { ClientMessageReq } from '@tassat/fred-definitions';
import React from 'react';

import Worker from 'Lib/worker/PrimusWorker';
import { useAppSelector } from 'Reducers/store/hooks';
import { getClientMessageById } from 'Selectors/clientMessages';
import ApproveClientMessage from 'SharedComponents/clientMessages/approveClientMessage';
import ClientMessageDetails from 'SharedComponents/clientMessages/clientMessageDetails';
import DeclineClientMessage from 'SharedComponents/clientMessages/declineClientMessage';

import type { ClientMessageDetailsModalProps } from '.';

export function ClientMessageDetailsModal (props: ClientMessageDetailsModalProps) {
    const {
        classes,
        open,
        handleClose,
        selectedMessageId
    } = props;

    const selectedMessage = useAppSelector((state) => getClientMessageById(state, selectedMessageId));
    const [ modalScreen, setModalScreen ] = React.useState<'details' | 'approve' | 'decline'>('details');
    const cancelConfirm = () => setModalScreen('details');
    const [ approvalError, setApprovalError ] = React.useState<string>('');

    const borderClass = React.useMemo(() => {
        return modalScreen === 'details' ? classes.detailsBorder
            : modalScreen === 'approve' ? classes.approvalBorder
                : modalScreen === 'decline' ? classes.declineBorder : '';
    }, [modalScreen]);

    if (!selectedMessage) {
        return <div />;
    }

    const approveMessage = async () => {
        try {
            const sendValues: Partial<ClientMessageReq> = {
                ClientMsgType: selectedMessage.ClientMsgType,
                Message: selectedMessage.Message,
                StartAt: selectedMessage.StartAt,
                EndAt: selectedMessage.EndAt,
                Status: ClientMessagesStatusEnum.ACTIVE
            };

            const response = await Worker.getInstance()
                .updateClientMessage(selectedMessage.MessageId, { ...sendValues });

            if (response.data && response.data.Status === 48) {
                setModalScreen('details');
            } else if (response.data && response.data.Status === 49 || doesWorkflowRejectCodeExist(response.data.Status)) {
                setApprovalError('Could not approve message. Please try again.');
            }
        } catch (error) {
            setApprovalError('Something went wrong approving message. Please try again.');
        }
    };

    const declineMessage = async () => {
        try {
            const sendValues: Partial<ClientMessageReq> = {
                ClientMsgType: selectedMessage.ClientMsgType,
                Message: selectedMessage.Message,
                StartAt: selectedMessage.StartAt,
                EndAt: selectedMessage.EndAt,
                Status: ClientMessagesStatusEnum.DECLINED
            };

            const response = await Worker.getInstance()
                .updateClientMessage(selectedMessage.MessageId, { ...sendValues });

            if (response.data && response.data.Status === 48) {
                setModalScreen('details');
            } else if (response.data && response.data.Status === 49 || doesWorkflowRejectCodeExist(response.data.Status)) {
                setApprovalError('Could not decline message. Please try again.');
            }
        } catch (error) {
            setApprovalError('Something went wrong approving message. Please try again.');
        }
    };

    return (
        <Dialog
            classes={{ paperWidthXl: classes.paperWidthXl }}
            maxWidth='xl'
            onClose={handleClose}
            open={open}
            transitionDuration={{
                enter: 0,
                exit: 0
            }}
        >
            <Grid
                className={`${classes.root} ${borderClass}`}
                container
                direction='column'
                spacing={0}
                wrap='nowrap'
            >
                <Grid
                    className={classes.cancelIcon}
                    item
                >
                    <CancelButtonBlack
                        onClick={handleClose}
                    />
                </Grid>
                {modalScreen === 'details' &&
                    <ClientMessageDetails
                        confirmApprove={() => {
                        setApprovalError('');
                        setModalScreen('approve');
                    }}
                        confirmDecline={() => {
                        setApprovalError('');
                        setModalScreen('decline');
                    }}
                        selectedMessage={selectedMessage}
                    />}
                {modalScreen === 'approve' &&
                    <>
                        <span className={classes.approvalHeader}>APPROVE</span>
                        <ApproveClientMessage
                            approvalError={approvalError}
                            approveMessage={approveMessage}
                            cancelConfirm={cancelConfirm}
                            selectedMessage={selectedMessage}
                        />
                    </>}
                {modalScreen === 'decline' &&
                    <>
                        <span className={classes.declineHeader}>DECLINE</span>
                        <DeclineClientMessage
                            approvalError={approvalError}
                            cancelConfirm={cancelConfirm}
                            declineMessage={declineMessage}
                            selectedMessage={selectedMessage}
                        />
                    </>}
            </Grid>
        </Dialog>
    );
}